import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { useData } from '../context/DataContext';
import './Contact.css';

// ============================================================
// EMAILJS SETUP — Replace these 3 values after creating account at emailjs.com
// Step 1: Sign up at https://www.emailjs.com
// Step 2: Add Email Service (Gmail) → copy Service ID
// Step 3: Create Email Template → copy Template ID
// Step 4: Go to Account → copy Public Key
// ============================================================
const EMAILJS_SERVICE_ID  = 'service_ek3hu2l';
const EMAILJS_TEMPLATE_ID = 'template_wwzkkqm';
const EMAILJS_PUBLIC_KEY  = '12-gFYdXq5poB82If';

// const productOptions = [
//   'MedCore — Clinical Supplies',
//   'Life-Assist — Home Healthcare',
//   'NeoCare — Mother & Baby',
//   'MotionPro — Plastic Supplies',
//   'SafeServe — Food Grade',
//   'Athlete Support — Sports & Fitness',
//   'Multiple Product Lines',
//   'Other / Custom Requirement',
// ];

const initialForm = { name: '', email: '', phone: '', company: '', product: '', message: '' };

export default function Contact() {
  const { companyInfo, productLines } = useData();
  const formRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim() || form.phone.length < 10) e.phone = 'Valid phone number required';
    if (!form.product) e.product = 'Please select a product line';
    if (!form.message.trim() || form.message.length < 20) e.message = 'Please write at least 20 characters';
    return e;
  };

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone,
          company: form.company || 'Not specified',
          product: form.product,
          message: form.message,
          to_email: companyInfo.email,
        },
        EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  return (
    <main className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <span className="section-tag">Get in Touch</span>
          <h1>We'd Love to Hear From You</h1>
          <p>Send us your product enquiry and our team will respond within 24 hours.</p>
        </div>
      </section>

      <section className="contact-body container">
        {/* Info Cards */}
        <div className="contact-info">
          <h3>Contact Information</h3>

          <div className="info-cards">
            <a href={`tel:${companyInfo.phone1.replace(/\s/g,'')}`} className="info-card">
              <div className="info-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.49 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.4 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.7A16 16 0 0 0 15.3 16.1l.8-.8a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <div>
                <span className="info-label">Phone</span>
                <span className="info-value">{companyInfo.phone1}</span>
                <span className="info-value">{companyInfo.phone2}</span>
              </div>
            </a>

            <a href={`mailto:${companyInfo.email}`} className="info-card">
              <div className="info-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </div>
              <div>
                <span className="info-label">Email</span>
                <span className="info-value">{companyInfo.email}</span>
              </div>
            </a>

            <a href={`https://wa.me/${companyInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="info-card whatsapp">
              <div className="info-card-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.555 4.116 1.528 5.845L0 24l6.335-1.652A11.954 11.954 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.652-.51-5.166-1.4l-.371-.22-3.761.98.998-3.662-.241-.379A9.956 9.956 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              </div>
              <div>
                <span className="info-label">WhatsApp</span>
                <span className="info-value">Chat with us directly</span>
              </div>
            </a>

            <div className="info-card address-card">
              <div className="info-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <span className="info-label">Address</span>
                <span className="info-value">{companyInfo.address}</span>
              </div>
            </div>
          </div>

          <div className="business-hours">
            <h4>Business Hours</h4>
            <div className="hours-row"><span>Monday – Saturday</span><span>9:00 AM – 6:00 PM</span></div>
            <div className="hours-row"><span>Sunday</span><span>Closed</span></div>
          </div>
        </div>

        {/* Form */}
        <div className="contact-form-wrap">
          {status === 'success' ? (
            <div className="success-state">
              <div className="success-icon">✅</div>
              <h3>Enquiry Sent Successfully!</h3>
              <p>Thank you <strong>{form.name || 'for reaching out'}</strong>. Our team will contact you within 24 business hours at <strong>{form.email}</strong>.</p>
              <button className="btn-primary" onClick={() => setStatus('idle')}>Send Another Enquiry</button>
            </div>
          ) : (
            <>
              <div className="form-header">
                <h3>Send Your Enquiry</h3>
                <p>Fill out the form and our team will get back to you promptly.</p>
              </div>
              <form ref={formRef} onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="form-row">
                  <div className={`form-group${errors.name ? ' error' : ''}`}>
                    <label>Full Name *</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
                    {errors.name && <span className="err-msg">{errors.name}</span>}
                  </div>
                  <div className={`form-group${errors.email ? ' error' : ''}`}>
                    <label>Email Address *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" />
                    {errors.email && <span className="err-msg">{errors.email}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className={`form-group${errors.phone ? ' error' : ''}`}>
                    <label>Phone Number *</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" />
                    {errors.phone && <span className="err-msg">{errors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <label>Firm / Hospital Name</label>
                    <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Optional" />
                  </div>
                </div>
                <div className={`form-group${errors.product ? ' error' : ''}`}>
                  <label>Product Line of Interest *</label> 
                  <select name="product" value={form.product} onChange={handleChange}>
                  <option value="">— Select a product line —</option>
                  {productLines.map(l => (
                    <option key={l.id} value={`${l.name} — ${l.subtitle}`}>
                     {l.name} — {l.subtitle}
                    </option>
   ))}
                    {/* <option value="">— Select a product line —</option>
                    {productOptions.map(p => <option key={p} value={p}>{p}</option>)} */}
                    
                  </select>
                  {errors.product && <span className="err-msg">{errors.product}</span>}
                </div>
                <div className={`form-group${errors.message ? ' error' : ''}`}>
                  <label>Your Message / Requirements *</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={5}
                    placeholder="Please describe what you're looking for — product name, quantity, specifications, etc." />
                  {errors.message && <span className="err-msg">{errors.message}</span>}
                </div>

                {status === 'error' && (
                  <div className="form-error-banner">
                    ⚠️ Something went wrong. Please try again or contact us directly at {companyInfo.email}
                  </div>
                )}

                <button type="submit" className="btn-submit" disabled={status === 'sending'}>
                  {status === 'sending' ? (
                    <><span className="spinner" /> Sending...</>
                  ) : (
                    <>Send Enquiry →</>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
